'use client';

import * as React from 'react';
import { Loader2, Mail } from 'lucide-react';
import { authClient } from '@/lib/auth-client';
import { SmsOtpInput } from './SmsOtpInput';
import { cn } from '@/lib/utils';
export interface EmailOtpFormProps {
  /** Destination after successful verify. Defaults to `/`. */
  callbackURL?: string;
  /** Optional external error sink (in addition to the inline alert). */
  onError?: (message: string) => void;
  className?: string;
}
type Step = 'email' | 'otp';
const RESEND_SECONDS = 60;
// Server-side authority — must match `otpLength` in lib/auth.ts emailOTP plugin.
const OTP_LEN = 4;

// Same regex as the server-side `isValidEmailFormat` in lib/email/send.ts.
// Done lightly on the client — server validates via better-auth (`z.email()`).
function isEmailFormatValid(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
const SEND_FALLBACK = 'Не удалось отправить код. Попробуйте позже';
const VERIFY_FALLBACK = 'Не удалось проверить код. Попробуйте позже';
const SEND_ERROR_MAP: Record<string, string> = {
  INVALID_EMAIL: 'Введите корректный email',
  // 429 rate-limit (built into better-auth)
  TOO_MANY_REQUESTS: 'Слишком много попыток. Попробуйте через минуту'
};
const VERIFY_ERROR_MAP: Record<string, string> = {
  'OTP expired': 'Код истёк, запросите новый',
  'Invalid OTP': 'Неверный код',
  'Too many attempts': 'Слишком много попыток. Запросите новый код',
  TOO_MANY_REQUESTS: 'Слишком много попыток. Попробуйте через минуту'
};
interface SendResult {
  ok: boolean;
  errorMessage?: string;
}
function pickError(map: Record<string, string>, raw: unknown, fallback: string): string {
  if (typeof raw !== 'string' || !raw) return fallback;
  return map[raw] ?? fallback;
}

// Pick the best error text from a Better Auth response. Priority:
//   1. our SEND_ERROR_MAP code mapping (translates TOO_MANY_REQUESTS / INVALID_EMAIL),
//   2. server-supplied `message` if it looks human-readable (our rejectDisposableDomain
//      sends a full Russian sentence; Better Auth's auto-generated codes are unreadable
//      slugs like `_EMAIL________GMAIL_YANDEX_MAILRU____GOOGLE_YANDEX_VK__TELEGRAM`),
//   3. fallback.
function pickAuthError(map: Record<string, string>, err: {
  code?: unknown;
  message?: unknown;
} | null | undefined, fallback: string): string {
  const code = typeof err?.code === 'string' ? err.code : undefined;
  if (code && map[code]) return map[code];
  const message = typeof err?.message === 'string' ? err.message.trim() : '';
  // Use server message only if it looks like a real sentence (has a space or Cyrillic).
  if (message && (/\s/.test(message) || /[Ѐ-ӿ]/.test(message))) return message;
  return fallback;
}

/**
 * Email-OTP login form. Two steps inside one card (no nested Dialog):
 *   1. email input → «Продолжить» → POST /api/auth/email-otp/send-verification-otp
 *   2. 4-digit OTP input → POST /api/auth/sign-in/email-otp → hard reload
 *
 * Mirrors `SmsLoginForm`'s flow & error UX deliberately so the modal feels uniform.
 *
 * Account linking: Better Auth's `findUserByEmail` lookup means an existing
 * Google/Yandex/VK profile with the same email gets the new session attached
 * to the same `user.id`. Trial-токены не задваиваются — `databaseHooks.user.create.after`
 * не срабатывает повторно.
 */
export function EmailOtpForm({
  callbackURL,
  onError,
  className
}: EmailOtpFormProps) {
  const [step, setStep] = React.useState<Step>('email');
  const [email, setEmail] = React.useState('');
  const [code, setCode] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [verifying, setVerifying] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [resendSeconds, setResendSeconds] = React.useState(0);
  const otpRef = React.useRef<HTMLInputElement>(null);
  const cancelledRef = React.useRef(false);
  React.useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);
  React.useEffect(() => {
    if (step === 'otp') {
      const id = requestAnimationFrame(() => {
        otpRef.current?.focus();
      });
      return () => cancelAnimationFrame(id);
    }
  }, [step]);
  React.useEffect(() => {
    if (step !== 'otp' || resendSeconds <= 0) return;
    const id_0 = setInterval(() => {
      setResendSeconds(s => s > 0 ? s - 1 : 0);
    }, 1000);
    return () => clearInterval(id_0);
  }, [step, resendSeconds]);
  const reportError = React.useCallback((msg: string) => {
    setError(msg);
    onError?.(msg);
  }, [onError]);
  const sendOtp = React.useCallback(async (target: string): Promise<SendResult> => {
    try {
      const res = await authClient.emailOtp.sendVerificationOtp({
        email: target,
        type: 'sign-in'
      });
      if (res.error) {
        return {
          ok: false,
          errorMessage: pickAuthError(SEND_ERROR_MAP, res.error, SEND_FALLBACK)
        };
      }
      return {
        ok: true
      };
    } catch {
      return {
        ok: false,
        errorMessage: SEND_FALLBACK
      };
    }
  }, []);
  const handleEmailSubmit = React.useCallback(async () => {
    const trimmed = email.trim().toLowerCase();
    if (!isEmailFormatValid(trimmed) || sending) return;
    setSending(true);
    setError(null);
    const result = await sendOtp(trimmed);
    if (cancelledRef.current) return;
    setSending(false);
    if (result.ok) {
      setEmail(trimmed);
      setStep('otp');
      setResendSeconds(RESEND_SECONDS);
      setCode('');
    } else if (result.errorMessage) {
      reportError(result.errorMessage);
    }
  }, [email, sending, sendOtp, reportError]);
  const handleResend = React.useCallback(async () => {
    if (resendSeconds > 0 || sending) return;
    setSending(true);
    setError(null);
    const result_0 = await sendOtp(email);
    if (cancelledRef.current) return;
    setSending(false);
    if (result_0.ok) {
      setResendSeconds(RESEND_SECONDS);
      setCode('');
    } else if (result_0.errorMessage) {
      reportError(result_0.errorMessage);
    }
  }, [email, resendSeconds, sending, sendOtp, reportError]);
  const handleChangeEmail = React.useCallback(() => {
    setStep('email');
    setCode('');
    setError(null);
    setResendSeconds(0);
  }, []);
  const verifyOtp = React.useCallback(async (submittedCode: string) => {
    if (verifying) return;
    setVerifying(true);
    setError(null);
    try {
      const res_0 = await authClient.signIn.emailOtp({
        email,
        otp: submittedCode
      });
      if (res_0.error) {
        const message = res_0.error.message as string | undefined || res_0.error.code as string | undefined;
        if (cancelledRef.current) return;
        setVerifying(false);
        const msg_0 = pickError(VERIFY_ERROR_MAP, message, VERIFY_FALLBACK);
        reportError(msg_0);
        // Reset on bad/expired code so user can retype.
        if (message === 'Invalid OTP' || message === 'OTP expired' || message === 'Too many attempts') {
          setCode('');
          requestAnimationFrame(() => otpRef.current?.focus());
        }
        return;
      }
      // Hard reload — same pattern as SmsLoginForm/TelegramLoginButton so
      // Set-Cookie is picked up and customSession recomputes EVA fields.
      window.location.href = callbackURL || '/';
    } catch {
      if (cancelledRef.current) return;
      setVerifying(false);
      reportError(VERIFY_FALLBACK);
    }
  }, [callbackURL, email, verifying, reportError]);
  const handleOtpComplete = React.useCallback((submittedCode_0: string) => {
    void verifyOtp(submittedCode_0);
  }, [verifyOtp]);
  const trimmedEmail = email.trim().toLowerCase();
  const submitDisabled = !isEmailFormatValid(trimmedEmail) || sending;
  return <div className={cn('flex flex-col gap-3', className)}>
      {step === 'email' ? <form onSubmit={e => {
      e.preventDefault();
      void handleEmailSubmit();
    }} className="flex flex-col gap-3" noValidate>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="sr-only">Электронная почта</span>
            <input type="email" inputMode="email" autoComplete="email" value={email} onChange={e_0 => setEmail(e_0.target.value)} placeholder="Введите почту" disabled={sending} aria-invalid={Boolean(error)} aria-label="Электронная почта"
        // iOS Safari зумит input при tap, если font-size < 16px (страница
        // приезжает влево, модалка частично уходит за viewport). Явный 16px
        // отключает auto-zoom — стандартный фикс.
        style={{
          fontSize: 16
        }} className="w-full px-4 py-3 bg-muted/50 border border-border rounded-full text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-colors disabled:opacity-50" />
          </label>

          {error && <p role="alert" aria-live="polite" className="text-sm text-destructive text-center">
              {error}
            </p>}

          <button type="submit" disabled={submitDisabled} className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
            {sending ? <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" /> : <Mail className="w-5 h-5" aria-hidden="true" />}
            Продолжить
          </button>
        </form> : <div className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground text-center">
            Код отправлен на <span className="text-foreground font-medium">{email}</span>
          </p>
          <p className="text-xs text-muted-foreground text-center">
            Если письма нет — проверьте папку «Спам»
          </p>

          <SmsOtpInput ref={otpRef} value={code} onChange={setCode} onComplete={handleOtpComplete} length={OTP_LEN} disabled={verifying} aria-invalid={Boolean(error)} aria-label="Код из письма" />

          {error && <p role="alert" aria-live="polite" className="text-sm text-destructive text-center">
              {error}
            </p>}

          {verifying && <p className="text-xs text-muted-foreground text-center animate-pulse">
              Проверяем код…
            </p>}

          <div className="flex flex-col items-center gap-2 text-sm">
            <button type="button" onClick={() => void handleResend()} disabled={resendSeconds > 0 || sending} className="text-foreground/70 hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
              {resendSeconds > 0 ? `Отправить ещё раз через ${resendSeconds}с` : 'Отправить ещё раз'}
            </button>
            <button type="button" onClick={handleChangeEmail} className="text-muted-foreground hover:text-foreground underline-offset-4 hover:underline cursor-pointer">
              Вернуться назад
            </button>
          </div>
        </div>}
    </div>;
}
export const __testConstants = {
  RESEND_SECONDS,
  OTP_LEN,
  SEND_ERROR_MAP,
  VERIFY_ERROR_MAP
};